import React, { useEffect, useState } from 'react'
import axios from 'axios'
import { ExternalLink } from 'lucide-react'
import { getAxiosDefaultConfig } from 'utils'
import { baseUrl } from '../../../modules/urls'

// Skill ID from the Amazon Developer Console.
// Update ALEXA_SKILL_URL below once the skill is published and has a store ASIN.
const SKILL_ID = 'amzn1.ask.skill.4b20cf09-0553-4035-9c7d-eaf8a6f5be10'

// While the skill is in development / beta, this links to the Alexa skill
// detail page in the web console. Once published, swap for the store URL:
//   https://www.amazon.com/dp/<ASIN>
// On mobile the Alexa app will intercept the amazon.com/dp link and open it
// in-app, so that URL doubles as the deeplink target post-publish.
const ALEXA_SKILL_URL = `https://alexa.amazon.com/spa/index.html#skills/${SKILL_ID}`

// Alexa ring SVG — matches the animated ring on the alexa-login page.
function AlexaRingIcon({ size = 40 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 40 40"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-label="Amazon Alexa"
    >
      <circle cx="20" cy="20" r="19" fill="#0a1a2f" />
      <circle cx="20" cy="20" r="14" stroke="rgba(0,202,255,0.2)" strokeWidth="2.5" fill="none" />
      <circle cx="20" cy="20" r="5" fill="#00caff" opacity="0.9" />
      <path
        d="M20 6 A14 14 0 0 1 34 20"
        stroke="#00caff"
        strokeWidth="2.5"
        strokeLinecap="round"
        fill="none"
      />
      <path
        d="M6 20 A14 14 0 0 1 20 6"
        stroke="#1a9fe8"
        strokeWidth="2.5"
        strokeLinecap="round"
        fill="none"
        opacity="0.4"
      />
    </svg>
  )
}

export default function AlexaSkillCard() {
  const [linked, setLinked] = useState<boolean | null>(null)

  useEffect(() => {
    axios
      .get<{ linked: boolean }>(`${baseUrl}/alexa/status`, getAxiosDefaultConfig())
      .then((res) => setLinked(res.data?.linked ?? false))
      .catch(() => setLinked(false))
  }, [])

  const handleOpen = () => {
    window.open(ALEXA_SKILL_URL, '_blank', 'noopener,noreferrer')
  }

  return (
    <div className="tw-col-span-1">
      <div className="tw-h-[240px] tw-flex tw-flex-col tw-rounded-lg tw-shadow-md tw-p-4 tw-bg-white hover:tw-ring hover:tw-ring-blue-600">
        {/* Header row */}
        <div className="tw-flex tw-items-center tw-justify-between tw-mb-4 tw-shrink-0">
          <div className="tw-flex tw-items-center tw-gap-2">
            <AlexaRingIcon size={40} />
            {/* Beta badge */}
            <span className="tw-text-xs tw-font-semibold tw-px-1.5 tw-py-0.5 tw-rounded tw-bg-amber-100 tw-text-amber-700 tw-border tw-border-amber-200">
              Beta
            </span>
          </div>

          <button
            type="button"
            onClick={handleOpen}
            className="tw-text-sm tw-cursor-pointer tw-rounded-md tw-border tw-border-slate-300 hover:tw-border-blue-600 hover:tw-bg-blue-600 hover:tw-text-white tw-transition-colors tw-duration-150 tw-shadow-sm tw-flex tw-items-center tw-space-x-2 tw-px-2.5 tw-py-1"
          >
            <ExternalLink size={13} />
            <span>{linked ? 'Open in Alexa' : 'Enable skill'}</span>
          </button>
        </div>

        {/* Body */}
        <div className="tw-flex tw-flex-col tw-min-h-0 tw-flex-1">
          <div className="tw-text-base tw-font-bold tw-mb-2 tw-shrink-0">Amazon Alexa</div>
          <div className="tw-text-slate-500 tw-text-sm tw-mb-3">
            Ask Alexa questions about your store. Query sales, orders, and customers by voice — powered by the PayHelm AI agent.
          </div>

          {/* Status pill */}
          {linked === true && (
            <div className="tw-flex tw-items-center tw-gap-1.5 tw-text-sm tw-text-emerald-700 tw-font-medium">
              <span className="tw-w-2 tw-h-2 tw-rounded-full tw-bg-emerald-500 tw-inline-block" />
              Account linked
            </div>
          )}
          {linked === false && (
            <div className="tw-flex tw-items-center tw-gap-1.5 tw-text-sm tw-text-slate-400">
              <span className="tw-w-2 tw-h-2 tw-rounded-full tw-bg-slate-300 tw-inline-block" />
              Not linked — enable the skill in the Alexa app
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
